/**
 * head.js
 * A utility for composing elements for the `<head>` of an HTML document.
 * All methods are meant to be consumed by `react-helmet`'s `<Helmet />`.
 */
import * as React from 'react';
import {isArray} from 'lodash';
import {getEnvironmentVariable, getOrigin} from '@albert-io/environment';

/**
 * Helper for creating a page `<title>`.
 * This will also return the proper title `<meta>` tag for OpenGraph.
 * @param {string | string[]} value
 * @returns {import('React').ReactNode}
 */
export function title(value?: string | string[]): React.ReactElement[] | null {
  if (!value) {
    return null;
  }

  let processedValue = value;
  if (isArray(processedValue)) {
    /**
     * If the value provided is an Array, we cast it to a string
     * using our standardized seperator.
     */
    processedValue = processedValue.join(' | ');
  }
  return [
    <title key='title'>{processedValue}</title>,
    <meta key='og:title' property='og:title' content={processedValue} />
  ];
}

export const meta = {
  /**
   * Helper for creating a page `<meta name='description'>`.
   * This will also return the proper description `<meta>` tag for OpenGraph.
   * @param {string} content
   * @returns {import('React').ReactNode}
   */
  description(content?: string): React.ReactElement[] | null {
    if (!content) {
      return null;
    }
    return [
      <meta key='description' name='description' content={content} />,
      <meta key='og:description' property='og:description' content={content} />
    ];
  }
};

/** The public URL for a route, independent of the host serving the build. */
export function publicUrl(path: string): string {
  const host = getEnvironmentVariable('ALBERT_CLIENT_HOST');
  const origin = host ? `https://${host}` : getOrigin();
  return `${origin}${path}`;
}

/** Canonical and Open Graph URLs for the same page. */
export function canonicalUrl(url?: string): React.ReactElement[] | null {
  if (!url) return null;
  return [
    <link key='canonical' rel='canonical' href={url} />,
    <meta key='og:url' property='og:url' content={url} />
  ];
}
